/**
 * Principal gating for the two brand surfaces (spec U3.4). The brand API admits an **ACTIVE
 * OWNER of the caller's own tenant OR a platform SUPER_ADMIN**, which makes THREE
 * operator-side outcomes, not two:
 *
 *  1. `isOwner` → the editor (`Outlet`).
 *  2. `isSuperAdmin` → admitted by the API, but the principal is **tenant-less** and every
 *     brand endpoint derives the tenant from the JWT, so there is nothing to call: render the
 *     "select a tenant" placeholder instead of firing a request that cannot succeed. The
 *     operator tenant-targeting mechanism is an open saas-auth contract item (spec's
 *     SUPER_ADMIN caveat; see the delivery's Architect notes).
 *  3. anything else — a `SAAS_ADMIN` operator or a tenant ADMIN/USER → access denied, which
 *     is what the API would answer anyway; rendering it client-side just says so plainly.
 *
 * Gates on the role-derived `isOwner`/`isSuperAdmin` flags, never on `kind` (which only
 * records which `/me` shape arrived).
 */
import { Outlet } from 'react-router-dom';
import { Box, Button, Container, Paper, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { AuthPending } from './AuthPending';
import { useAuth } from './AuthContext';
import { useCopy } from '@/hooks/useCopy';

export function BrandAccessGate() {
  const { principal, principalStatus, bootstrapPrincipal } = useAuth();
  const { t } = useCopy();

  if (principalStatus === 'pending') {
    return <AuthPending message={t('app.loading', 'Loading')} />;
  }

  // `/me` failed for a reason the HTTP client did not already turn into a re-login (a 5xx,
  // a network drop): the session is live but the identity is unknown, so offer a retry
  // rather than guessing at an authority level.
  if (principalStatus === 'error' || !principal) {
    return (
      <Notice
        title={t('access.principalErrorTitle', "We couldn't load your account")}
        body={t(
          'access.principalErrorBody',
          'You are signed in, but your account details could not be loaded. Try again.',
        )}
        action={
          <Button variant="contained" onClick={() => void bootstrapPrincipal()}>
            {t('common.retry', 'Retry')}
          </Button>
        }
      />
    );
  }

  if (principal.isOwner) return <Outlet />;

  if (principal.isSuperAdmin) {
    return (
      <Notice
        title={t('access.operatorTitle', 'Platform operator')}
        body={t(
          'access.operatorBody',
          'Platform operator: select a tenant to manage its brand — not yet available.',
        )}
      />
    );
  }

  return (
    <Notice
      title={t('access.deniedTitle', 'You do not have access to brand settings')}
      body={t(
        'access.deniedBody',
        'Only an active owner of your tenant can edit login branding and email templates. Ask an owner of your tenant for access.',
      )}
    />
  );
}

/** The gate's non-editor states: one heading, one explanation, an optional action. */
function Notice({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return (
    <Container maxWidth="sm" disableGutters sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }} elevation={1}>
        <Typography variant="h1" component="h1" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {body}
        </Typography>
        {action ? <Box sx={{ mt: 3 }}>{action}</Box> : null}
      </Paper>
    </Container>
  );
}
